import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Gamepad2, Users, Play, Trophy, Clock, QrCode,
  ChevronRight, Crown, Zap, CheckCircle, XCircle,
  SkipForward, StopCircle, Hash, Target, Medal, Frown,
  History, Calendar, ArrowLeft, Eye, BarChart3, Tag
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const KahootHostPage = ({ user }) => {
  const [step, setStep] = useState('setup'); // setup, history, history_detail, lobby, playing, results, podium, finished
  const [themes, setThemes] = useState([]);
  const [selectedThemes, setSelectedThemes] = useState([]);
  const [questionCount, setQuestionCount] = useState(10);
  const [timePerQuestion, setTimePerQuestion] = useState(10);
  const [gameData, setGameData] = useState(null);
  const [players, setPlayers] = useState([]);
  const [pastSessions, setPastSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionIdx, setQuestionIdx] = useState(-1);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [answeredCount, setAnsweredCount] = useState(0);
  const [questionResults, setQuestionResults] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [timer, setTimer] = useState(0);
  const [showQR, setShowQR] = useState(true);
  const wsRef = useRef(null);
  const timerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get('/foad/question-bank/themes').then(r => setThemes(r.data)).catch(() => {});
    loadHistory();
    return () => { if (wsRef.current) wsRef.current.close(); if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  const loadHistory = async () => {
    try { const r = await axios.get('/foad/kahoot/my-sessions'); setPastSessions(r.data); } catch {}
  };

  const viewSessionDetail = async (sessionId) => {
    try {
      const r = await axios.get(`/foad/kahoot/history/${sessionId}`);
      setSelectedSession(r.data);
      setStep('history_detail');
    } catch { toast.error('Erreur chargement session'); }
  };

  const createGame = async () => {
    if (selectedThemes.length === 0) { toast.error('Selectionnez au moins un theme'); return; }
    try {
      const r = await axios.post('/foad/kahoot/create', {
        theme_ids: selectedThemes, question_count: questionCount,
        time_per_question: timePerQuestion, types: ['qcm', 'vrai_faux']
      });
      setGameData(r.data);
      setTotalQuestions(r.data.question_count);
      connectWebSocket(r.data.game_code);
      setStep('lobby');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur creation'); }
  };

  const connectWebSocket = (gameCode) => {
    const wsProtocol = BACKEND_URL.startsWith('https') ? 'wss' : 'ws';
    const wsHost = BACKEND_URL.replace(/^https?:\/\//, '');
    const ws = new WebSocket(`${wsProtocol}://${wsHost}/api/foad/kahoot/ws/teacher/${gameCode}`);
    wsRef.current = ws;

    // Client-side keepalive to prevent proxy timeout
    let keepaliveId = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ action: 'ping' }));
      }
    }, 15000);

    ws.onmessage = (event) => {
      const msg = JSON.parse(event.data);
      if (msg.type === 'ping' || msg.type === 'pong') return;
      if (msg.type === 'game_state') {
        setPlayers(msg.players || []);
        setTotalQuestions(msg.total_questions);
      } else if (msg.type === 'player_joined') {
        setPlayers(msg.players || []);
      } else if (msg.type === 'question') {
        setCurrentQuestion(msg);
        setQuestionIdx(msg.question_idx);
        setAnsweredCount(0);
        setQuestionResults(null);
        setStep('playing');
        startTimer(msg.time_limit);
      } else if (msg.type === 'answer_received') {
        setAnsweredCount(msg.answered_count);
      } else if (msg.type === 'question_results') {
        setQuestionResults(msg);
        setLeaderboard(msg.leaderboard || []);
        setStep('results');
        if (timerRef.current) clearInterval(timerRef.current);
      } else if (msg.type === 'game_over') {
        setLeaderboard(msg.leaderboard || []);
        setStep('finished');
        if (timerRef.current) clearInterval(timerRef.current);
      }
    };

    ws.onerror = () => toast.error('Erreur connexion');
    ws.onclose = () => { clearInterval(keepaliveId); };
  };

  const startTimer = (seconds) => {
    setTimer(seconds);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setTimer(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const sendAction = (action) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ action }));
    }
  };

  const joinUrl = gameData ? `${BACKEND_URL}/foad-kahoot-play?code=${gameData.game_code}` : '';
  const qrUrl = gameData ? `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(joinUrl)}` : '';

  // ============= SETUP =============
  if (step === 'setup' || step === 'history') {
    const isHistory = step === 'history';
    return (
      <div className="max-w-2xl mx-auto space-y-6" data-testid="kahoot-setup">
        <div className="text-center py-6 relative">
          <button onClick={() => navigate('/foad')} className="absolute left-0 top-6 p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition-colors" data-testid="back-to-foad-btn">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <Gamepad2 className="h-12 w-12 text-violet-600 mx-auto mb-3" />
          <h1 className="text-3xl font-black text-slate-800">Kahoot Live</h1>
          <p className="text-slate-500 mt-1">Creez un quiz interactif en temps reel</p>
        </div>

        {/* Tab bar */}
        <div className="flex bg-slate-100 rounded-lg p-1">
          <button onClick={() => setStep('setup')} className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium transition-colors ${!isHistory ? 'bg-white shadow text-violet-700' : 'text-slate-500 hover:text-slate-700'}`} data-testid="tab-new-game">
            <Zap className="h-4 w-4" /> Nouvelle partie
          </button>
          <button onClick={() => { setStep('history'); loadHistory(); }} className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium transition-colors ${isHistory ? 'bg-white shadow text-violet-700' : 'text-slate-500 hover:text-slate-700'}`} data-testid="tab-history">
            <History className="h-4 w-4" /> Historique
          </button>
        </div>

        {!isHistory ? (
          <div className="bg-white rounded-xl border p-6 space-y-5">
            <div>
              <label className="text-sm font-semibold text-slate-700 mb-2 block">Themes des questions</label>
              <div className="flex flex-wrap gap-2">
                {themes.map(t => (
                  <button key={t.theme_id} onClick={() => setSelectedThemes(prev => prev.includes(t.theme_id) ? prev.filter(x => x !== t.theme_id) : [...prev, t.theme_id])}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${selectedThemes.includes(t.theme_id) ? 'bg-violet-600 text-white shadow-md shadow-violet-200' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                    data-testid={`select-theme-${t.theme_id}`}>
                    {selectedThemes.includes(t.theme_id) && <CheckCircle className="h-4 w-4" />}
                    {t.name} <span className="opacity-70">({t.question_count})</span>
                  </button>
                ))}
                {themes.length === 0 && <p className="text-sm text-slate-400">Aucun theme disponible. Creez des themes dans la Banque de Questions.</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-semibold text-slate-700 mb-1 block">Nombre de questions</label>
                <input type="number" min={1} max={30} value={questionCount} onChange={e => setQuestionCount(parseInt(e.target.value) || 10)} className="w-full border rounded-lg px-3 py-2.5 text-sm" data-testid="question-count-input" />
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700 mb-1 block">Temps par question (sec)</label>
                <input type="number" min={5} max={30} value={timePerQuestion} onChange={e => setTimePerQuestion(parseInt(e.target.value) || 10)} className="w-full border rounded-lg px-3 py-2.5 text-sm" data-testid="time-input" />
              </div>
            </div>

            <Button onClick={createGame} className="w-full bg-violet-600 hover:bg-violet-700 text-white py-6 text-lg font-bold" data-testid="create-game-btn">
              <Zap className="h-5 w-5 mr-2" /> Lancer la partie
            </Button>
          </div>
        ) : (
          /* History list */
          <div className="bg-white rounded-xl border overflow-hidden" data-testid="kahoot-history">
            {pastSessions.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <History className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm">Aucune partie jouee pour le moment</p>
              </div>
            ) : (
              <div className="divide-y">
                {pastSessions.map(s => {
                  const d = s.created_at ? new Date(s.created_at) : null;
                  const dateStr = d ? d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
                  const top1 = s.leaderboard?.[0];
                  return (
                    <div key={s.session_id} className="p-4 hover:bg-slate-50 cursor-pointer transition-colors" onClick={() => viewSessionDetail(s.session_id)} data-testid={`session-${s.session_id}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${s.status === 'finished' ? 'bg-green-100 text-green-700' : s.status === 'waiting' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                              {s.status === 'finished' ? 'Terminee' : s.status === 'waiting' ? 'En attente' : 'En cours'}
                            </span>
                            <span className="text-xs text-slate-400 flex items-center gap-1"><Calendar className="h-3 w-3" /> {dateStr}</span>
                          </div>
                          <div className="flex items-center gap-3 text-sm">
                            <span className="flex items-center gap-1 text-slate-600">
                              <Tag className="h-3.5 w-3.5 text-violet-500" /> {(s.theme_names || []).join(', ') || 'N/A'}
                            </span>
                            <span className="flex items-center gap-1 text-slate-500">
                              <Users className="h-3.5 w-3.5" /> {s.player_count || 0} joueurs
                            </span>
                            <span className="flex items-center gap-1 text-slate-500">
                              <Hash className="h-3.5 w-3.5" /> {s.questions_count || 0} questions
                            </span>
                          </div>
                          {top1 && (
                            <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                              <Crown className="h-3 w-3" /> {top1.nickname} - {top1.score} pts
                            </p>
                          )}
                        </div>
                        <ChevronRight className="h-5 w-5 text-slate-300" />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // ============= HISTORY DETAIL =============
  if (step === 'history_detail' && selectedSession) {
    const s = selectedSession;
    const d = s.created_at ? new Date(s.created_at) : null;
    const dateStr = d ? d.toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
    const lb = s.leaderboard || [];
    const qStats = s.question_stats || [];

    return (
      <div className="max-w-3xl mx-auto space-y-6" data-testid="kahoot-history-detail">
        <button onClick={() => setStep('history')} className="flex items-center gap-2 text-sm text-slate-500 hover:text-violet-600 transition-colors" data-testid="back-to-history">
          <ArrowLeft className="h-4 w-4" /> Retour a l'historique
        </button>

        {/* Header */}
        <div className="bg-gradient-to-br from-violet-600 to-purple-700 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xl font-black">Session Kahoot</h2>
            <span className="text-xs bg-white/20 px-3 py-1 rounded-full">{s.game_code}</span>
          </div>
          <p className="text-violet-200 text-sm">{dateStr}</p>
          <div className="flex items-center gap-6 mt-4 text-sm">
            <span className="flex items-center gap-1.5"><Tag className="h-4 w-4 text-violet-300" /> {(s.theme_names || []).join(', ')}</span>
            <span className="flex items-center gap-1.5"><Users className="h-4 w-4 text-violet-300" /> {s.player_count || lb.length} joueurs</span>
            <span className="flex items-center gap-1.5"><Hash className="h-4 w-4 text-violet-300" /> {s.questions_count || qStats.length} questions</span>
          </div>
        </div>

        {/* Podium top 3 */}
        {lb.length >= 1 && (
          <div className="bg-white rounded-xl border p-6">
            <h3 className="font-bold text-slate-700 flex items-center gap-2 mb-4"><Trophy className="h-5 w-5 text-amber-500" /> Podium</h3>
            <div className="flex items-end justify-center gap-6 h-40">
              {[1, 0, 2].map(pos => {
                const p = lb[pos];
                if (!p) return <div key={pos} className="w-24" />;
                const colors = ['bg-amber-400', 'bg-slate-400', 'bg-amber-700'];
                const heights = ['h-36', 'h-28', 'h-20'];
                const labels = ['1er', '2eme', '3eme'];
                return (
                  <div key={pos} className="text-center">
                    <div className={`${colors[pos]} ${heights[pos]} w-24 rounded-t-xl flex flex-col items-center justify-end pb-2 text-white`}>
                      <p className="font-bold text-sm truncate px-1">{p.nickname}</p>
                      <p className="text-lg font-black">{p.score}</p>
                      <p className="text-xs opacity-80">{labels[pos]}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Full ranking */}
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="p-4 bg-slate-50 border-b">
            <h3 className="font-bold text-slate-700 flex items-center gap-2"><BarChart3 className="h-4 w-4" /> Classement complet</h3>
          </div>
          <div className="divide-y">
            {lb.map((p, i) => (
              <div key={i} className={`flex items-center justify-between px-4 py-3 ${i < 3 ? 'bg-amber-50/30' : ''}`} data-testid={`history-rank-${i + 1}`}>
                <div className="flex items-center gap-3">
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-amber-400 text-white' : i === 1 ? 'bg-slate-400 text-white' : i === 2 ? 'bg-amber-700 text-white' : 'bg-slate-100 text-slate-600'}`}>
                    {i + 1}
                  </span>
                  <div>
                    <p className="font-semibold text-sm">{p.nickname}</p>
                    <p className="text-xs text-slate-500">{p.correct_count || 0} bonnes reponses</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-violet-600">{p.score} pts</p>
                  <p className="text-xs text-slate-400">{p.avg_time ? `${Math.round(p.avg_time)}ms moy.` : ''}</p>
                </div>
              </div>
            ))}
            {lb.length === 0 && <div className="text-center py-8 text-slate-400 text-sm">Aucun joueur dans cette session</div>}
          </div>
        </div>

        {/* Question stats */}
        {qStats.length > 0 && (
          <div className="bg-white rounded-xl border overflow-hidden">
            <div className="p-4 bg-slate-50 border-b">
              <h3 className="font-bold text-slate-700 flex items-center gap-2"><BarChart3 className="h-4 w-4" /> Statistiques par question</h3>
            </div>
            <div className="divide-y">
              {qStats.map((q, i) => {
                const pct = q.total_answered > 0 ? Math.round((q.correct_count / q.total_answered) * 100) : 0;
                return (
                  <div key={i} className="px-4 py-3">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-medium text-slate-700">Q{i + 1}. {q.texte}</p>
                      <span className={`text-xs font-bold ${pct >= 70 ? 'text-green-600' : pct >= 40 ? 'text-amber-600' : 'text-red-600'}`}>{pct}%</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${pct >= 70 ? 'bg-green-500' : pct >= 40 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-xs text-slate-400">{q.correct_count}/{q.total_answered}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }


  // ============= LOBBY =============
  if (step === 'lobby') {
    return (
      <div className="max-w-3xl mx-auto" data-testid="kahoot-lobby">
        <div className="bg-gradient-to-br from-violet-600 to-purple-700 rounded-2xl p-8 text-white text-center shadow-xl">
          <h2 className="text-2xl font-black mb-1">En attente des joueurs...</h2>
          <p className="text-violet-200 text-sm mb-6">Les eleves scannent le QR code ou entrent le code</p>

          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 inline-block mb-6">
            <p className="text-violet-200 text-xs uppercase tracking-widest mb-2">Code de la partie</p>
            <p className="text-5xl font-black tracking-[0.3em] text-white" data-testid="game-code">{gameData?.game_code}</p>
          </div>

          <div className="mb-6">
            {showQR && qrUrl && (
              <div className="bg-white rounded-xl p-4 inline-block">
                <img src={qrUrl} alt="QR Code" className="w-48 h-48" data-testid="qr-code" />
              </div>
            )}
            <p className="text-violet-200 text-xs mt-2">{joinUrl}</p>
          </div>

          <div className="bg-white/10 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-center gap-2 mb-3">
              <Users className="h-5 w-5" />
              <span className="font-bold text-lg" data-testid="player-count">{players.length} joueur(s)</span>
            </div>
            <div className="flex flex-wrap justify-center gap-2">
              {players.map(p => (
                <span key={p.player_id} className="bg-white/20 px-3 py-1.5 rounded-full text-sm font-medium">{p.nickname}</span>
              ))}
            </div>
          </div>

          <Button onClick={() => sendAction('start_game')} disabled={players.length === 0}
            className="bg-green-500 hover:bg-green-600 text-white px-10 py-5 text-lg font-bold shadow-lg" data-testid="start-game-btn">
            <Play className="h-5 w-5 mr-2" /> Demarrer ({players.length} joueurs)
          </Button>
        </div>
      </div>
    );
  }

  // ============= PLAYING (QUESTION DISPLAY) =============
  if (step === 'playing' && currentQuestion) {
    const opts = currentQuestion.options || [];
    const colors = ['bg-red-500', 'bg-blue-500', 'bg-amber-500', 'bg-green-500'];
    const shapes = ['triangle', 'diamond', 'circle', 'square'];

    return (
      <div className="max-w-4xl mx-auto" data-testid="kahoot-playing">
        {/* Timer + Progress */}
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm font-bold text-slate-500">Question {questionIdx + 1}/{totalQuestions}</span>
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full font-bold text-lg ${timer <= 3 ? 'bg-red-100 text-red-600 animate-pulse' : 'bg-violet-100 text-violet-700'}`}>
            <Clock className="h-5 w-5" /> {timer}s
          </div>
          <span className="text-sm text-slate-500" data-testid="answered-count">{answeredCount}/{players.length} reponses</span>
        </div>

        {/* Question */}
        <div className="bg-slate-900 rounded-2xl p-8 mb-6 text-center shadow-xl">
          <p className="text-white text-2xl font-bold leading-relaxed" data-testid="question-text">{currentQuestion.texte}</p>
        </div>

        {/* Answer options */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          {opts.map((o, i) => (
            <div key={i} className={`${colors[i % 4]} rounded-xl p-6 text-white text-center shadow-lg`}>
              <p className="text-xl font-bold">{o.texte}</p>
            </div>
          ))}
        </div>

        {/* Action buttons */}
        <div className="flex justify-center gap-3">
          <Button variant="outline" onClick={() => sendAction('show_results')} className="border-violet-300 text-violet-600" data-testid="show-results-btn">
            <Target className="h-4 w-4 mr-2" /> Voir les resultats
          </Button>
          <Button variant="outline" className="text-red-500 border-red-200" onClick={() => sendAction('end_game')} data-testid="end-game-btn">
            <StopCircle className="h-4 w-4 mr-2" /> Terminer
          </Button>
        </div>
      </div>
    );
  }

  // ============= RESULTS (AFTER EACH QUESTION) =============
  if (step === 'results' && questionResults) {
    const correct = questionResults.correct_answer || [];
    const opts = currentQuestion?.options || [];

    return (
      <div className="max-w-4xl mx-auto" data-testid="kahoot-results">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-black text-slate-800">Resultats</h2>
          <p className="text-slate-500">Question {questionIdx + 1}/{totalQuestions}</p>
        </div>

        {/* Question recap */}
        <div className="bg-slate-900 rounded-xl p-4 mb-6 text-center">
          <p className="text-white text-lg font-semibold">{currentQuestion?.texte}</p>
        </div>

        {/* Answer stats */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          {opts.map((o, i) => {
            const isCorrect = correct.includes(o.idx !== undefined ? o.idx : i);
            const colors = ['bg-red-500', 'bg-blue-500', 'bg-amber-500', 'bg-green-500'];
            return (
              <div key={i} className={`${colors[i % 4]} rounded-xl p-4 text-white relative ${isCorrect ? 'ring-4 ring-green-300' : 'opacity-60'}`}>
                <p className="text-lg font-bold">{o.texte}</p>
                {isCorrect && <CheckCircle className="absolute top-3 right-3 h-6 w-6 text-green-200" />}
              </div>
            );
          })}
        </div>

        {/* Stats */}
        <div className="bg-white rounded-xl border p-6 mb-6">
          <div className="flex items-center justify-center gap-8 mb-4">
            <div className="text-center">
              <p className="text-3xl font-black text-green-600">{questionResults.correct_count}</p>
              <p className="text-xs text-slate-500">Bonnes reponses</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-black text-red-500">{questionResults.total_players - questionResults.correct_count}</p>
              <p className="text-xs text-slate-500">Mauvaises reponses</p>
            </div>
          </div>

          {/* Full leaderboard */}
          {leaderboard.length > 0 && (
            <div>
              <h3 className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-2"><Trophy className="h-4 w-4 text-amber-500" /> Classement ({leaderboard.length} joueurs)</h3>
              {leaderboard.map((p, i) => (
                <div key={p.player_id} className={`flex items-center justify-between py-2 border-b last:border-0 ${i < 3 ? 'bg-amber-50/50' : ''}`}>
                  <div className="flex items-center gap-3">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-amber-400 text-white' : i === 1 ? 'bg-slate-400 text-white' : i === 2 ? 'bg-amber-700 text-white' : 'bg-slate-100 text-slate-600'}`}>{i + 1}</span>
                    <span className="font-medium text-sm">{p.nickname}</span>
                  </div>
                  <div className="text-right">
                    <span className="font-bold text-violet-600">{p.score} pts</span>
                    <span className="text-xs text-slate-400 ml-2">{p.correct_count || 0} bonne(s) - {p.avg_time || 0}s/q</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-center gap-3">
          <Button onClick={() => sendAction('next_question')} className="bg-violet-600 hover:bg-violet-700 text-white px-8 py-5 text-lg font-bold" data-testid="next-question-btn">
            {questionIdx + 1 >= totalQuestions ? (
              <><Trophy className="h-5 w-5 mr-2" /> Voir le podium</>
            ) : (
              <><ChevronRight className="h-5 w-5 mr-2" /> Question suivante</>
            )}
          </Button>
        </div>
      </div>
    );
  }

  // ============= FINAL PODIUM =============
  if (step === 'finished') {
    const podiumColors = ['from-amber-400 to-amber-500', 'from-slate-400 to-slate-500', 'from-amber-700 to-amber-800'];
    const podiumLabels = ['1er', '2eme', '3eme'];
    const podiumIcons = [Crown, Medal, Medal];
    const top3 = leaderboard.slice(0, 3);
    const rest = leaderboard.slice(3);

    return (
      <div className="max-w-3xl mx-auto" data-testid="kahoot-podium">
        <div className="text-center py-6">
          <Trophy className="h-14 w-14 text-amber-500 mx-auto mb-3 animate-bounce" />
          <h1 className="text-4xl font-black text-slate-800">Classement Final</h1>
          <p className="text-slate-500 mt-1">{totalQuestions} questions - {leaderboard.length} joueurs</p>
        </div>

        {/* Podium */}
        <div className="flex items-end justify-center gap-4 mb-8 h-64">
          {[1, 0, 2].map(pos => {
            const p = top3[pos];
            if (!p) return <div key={pos} className="w-32" />;
            const PIcon = podiumIcons[pos];
            const heights = ['h-52', 'h-40', 'h-32'];
            return (
              <div key={pos} className="text-center">
                <div className={`bg-gradient-to-b ${podiumColors[pos]} rounded-t-xl ${heights[pos]} w-32 flex flex-col items-center justify-end pb-4 text-white shadow-lg relative`}>
                  <div className="absolute -top-6">
                    <div className={`w-12 h-12 rounded-full bg-white shadow flex items-center justify-center ${pos === 0 ? 'ring-4 ring-amber-300' : ''}`}>
                      <PIcon className={`h-6 w-6 ${pos === 0 ? 'text-amber-500' : pos === 1 ? 'text-slate-500' : 'text-amber-700'}`} />
                    </div>
                  </div>
                  <p className="font-black text-lg mt-4">{p.nickname}</p>
                  <p className="text-2xl font-black">{p.score}</p>
                  <p className="text-xs opacity-80">{p.correct_count} bonnes - {p.avg_time || 0}s/q</p>
                  <p className="text-sm font-bold mt-1 bg-white/20 px-3 py-0.5 rounded-full">{podiumLabels[pos]}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Full ranking */}
        <div className="bg-white rounded-xl border overflow-hidden mb-6">
          <div className="p-4 bg-slate-50 border-b">
            <h3 className="font-bold text-slate-700 flex items-center gap-2"><Hash className="h-4 w-4" /> Classement complet</h3>
          </div>
          <div className="divide-y">
            {leaderboard.map((p, i) => (
              <div key={p.player_id} className={`flex items-center justify-between px-4 py-3 ${i < 3 ? 'bg-amber-50/50' : ''}`} data-testid={`rank-${i + 1}`}>
                <div className="flex items-center gap-3">
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${i === 0 ? 'bg-amber-400 text-white' : i === 1 ? 'bg-slate-400 text-white' : i === 2 ? 'bg-amber-700 text-white' : 'bg-slate-100 text-slate-600'}`}>
                    {i + 1}
                  </span>
                  <div>
                    <p className="font-semibold text-sm">{p.nickname}</p>
                    <p className="text-xs text-slate-500">{p.correct_count}/{totalQuestions} bonnes reponses</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-violet-600">{p.score} pts</p>
                  <p className="text-xs text-slate-400">moy. {p.avg_time || 0}s/question - total {p.total_time || 0}s</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="text-center">
          <Button onClick={() => { setStep('setup'); setGameData(null); setPlayers([]); setLeaderboard([]); if (wsRef.current) wsRef.current.close(); }}
            className="bg-violet-600 hover:bg-violet-700 text-white px-8" data-testid="new-game-btn">
            <Gamepad2 className="h-4 w-4 mr-2" /> Nouvelle partie
          </Button>
        </div>
      </div>
    );
  }

  return null;
};

export default KahootHostPage;
